import { ArrowLeft, Upload } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ClickSpark } from '@/components/react-bits/ClickSpark';
import logoUrl from '@/assets/Logo_Only.png';
import { isMockMode, mockModeKind } from '@/lib/api';
import { EM_DASH, formatTimestamp } from '@/lib/format';
import { useDataFreshness } from './DataFreshnessContext';

/**
 * The app chrome: a single black header bar, no sidebar.
 *
 * The dashboard is one destination (`/funds`) plus one operator screen
 * (`/admin`, gated behind `/login`) — a sidebar/tab-bar for two screens added
 * width without adding navigation value. Instead the header carries:
 *   - the Simas Jiwa logo + wordmark, top-left, before the title,
 *   - the live data-freshness stamp,
 *   - a single context-aware control, top-right: "Upload" while looking at
 *     the public dashboard, or "Kembali" (back to the dashboard) while on the
 *     upload/login screens.
 */

function titleForPath(pathname: string): string {
  if (pathname.startsWith('/admin')) return 'Upload Data';
  if (pathname.startsWith('/login')) return 'Masuk';
  return 'Siji Fixed Link';
}

function FreshnessStamp() {
  const freshness = useDataFreshness();
  return (
    <div className="text-right leading-tight hidden sm:block" aria-live="polite">
      <p className="text-white/45 text-[9px] uppercase tracking-wider">Data per</p>
      <p
        className="text-white/85 text-[11px] font-semibold whitespace-nowrap"
        style={{ fontFamily: 'var(--font-mono)' }}
        title={freshness ?? undefined}
      >
        {freshness ? formatTimestamp(freshness) : EM_DASH}
      </p>
    </div>
  );
}

function ContextNavButton({ pathname }: { pathname: string }) {
  const onUploadFlow = pathname.startsWith('/admin') || pathname.startsWith('/login');

  if (onUploadFlow) {
    return (
      <Link
        to="/funds"
        className="flex items-center gap-1.5 rounded-full pl-2.5 pr-3 py-1.5 transition-colors hover:bg-white/10 no-underline"
        style={{ border: '1px solid rgba(255,255,255,0.18)' }}
      >
        <ArrowLeft className="size-3.5 text-white/80" aria-hidden="true" />
        <span className="text-white/80 text-xs font-semibold whitespace-nowrap">Kembali ke Funds</span>
      </Link>
    );
  }

  return (
    <Link
      to="/admin"
      className="flex items-center gap-1.5 rounded-full pl-2.5 pr-3 py-1.5 transition-colors hover:bg-white/10 no-underline"
      style={{ border: '1px solid rgba(255,255,255,0.18)' }}
    >
      <Upload className="size-3.5 text-white/80" aria-hidden="true" />
      <span className="text-white/80 text-xs font-semibold whitespace-nowrap">Upload</span>
    </Link>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const title = titleForPath(location.pathname);

  return (
    <div
      className="app-shell bg-background"
      style={{ height: '100dvh', maxWidth: '100vw', overflow: 'hidden' }}
    >
      <div className="content-col">
        <header
          className="flex items-center justify-between gap-3 px-4 py-3 shrink-0 relative"
          style={{
            background: '#1A1919',
            paddingTop: 'max(0.75rem, env(safe-area-inset-top))',
          }}
        >
          <Link to="/funds" className="flex items-center gap-2.5 min-w-0 no-underline" aria-label="Siji Fixed Link">
            <img src={logoUrl} alt="Simas Jiwa" style={{ height: 28, width: 'auto' }} className="shrink-0" />
            <span
              className="hidden md:inline text-white font-semibold text-sm truncate"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {title}
            </span>
          </Link>

          <div className="flex items-center gap-3 shrink-0">
            <FreshnessStamp />
            <ContextNavButton pathname={location.pathname} />
          </div>
        </header>

        {isMockMode() ? (
          <div className="shrink-0 px-4 py-1.5" style={{ background: 'rgba(234,27,26,0.08)' }}>
            <p className="text-[10px] text-center" style={{ color: '#B4130F' }}>
              <strong className="font-bold uppercase tracking-wider">Mode contoh</strong>{' '}
              {mockModeKind() === 'error'
                ? '— semua permintaan sengaja digagalkan (?mock=error).'
                : '— data berasal dari fixture lokal (?mock=1).'}
            </p>
          </div>
        ) : null}

        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <ClickSpark sparkColor="#EA1B1A" sparkCount={7} sparkRadius={16} duration={380}>
            {children}
          </ClickSpark>
        </main>
      </div>
    </div>
  );
}
